import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, MessageCircle, RotateCcw } from 'lucide-react';
import { GlassCard } from '@/components/shared/GlassCard';
import { Button } from '@/components/ui/button';
import { brand } from '@/lib/theme/tokens';

type Props = {
  children: ReactNode;
};

type State = {
  error: Error | null;
};

/**
 * Top-level error boundary. Renders a branded recovery screen with a Try Again
 * button (resets internal state) and a link home, so an uncaught render error
 * never shows a blank page.
 *
 * Errors are logged to the console — a future hook can ship them to Sentry/etc.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    if (typeof console !== 'undefined') {
      console.error('[ErrorBoundary]', error, info.componentStack);
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  override render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="mx-auto flex min-h-[70vh] max-w-3xl items-center px-4 py-12 md:px-6">
        <GlassCard
          className="relative w-full overflow-hidden p-8 md:p-12"
          style={{ borderColor: `${brand.err}55` }}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background: `radial-gradient(60% 80% at 100% 0%, ${brand.err}22, transparent 60%), radial-gradient(60% 80% at 0% 100%, ${brand.saffron}1a, transparent 60%)`,
            }}
          />
          <div className="relative">
            <div
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border"
              style={{ background: `${brand.err}1c`, borderColor: `${brand.err}55`, color: brand.err }}
            >
              <AlertTriangle size={20} />
            </div>
            <div className="mt-4 text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: brand.err }}>
              Something broke
            </div>
            <h1 className="font-display mt-2 text-3xl font-semibold leading-tight tracking-tight text-ink md:text-4xl">
              That trail just{' '}
              <em
                className="font-medium italic"
                style={{
                  display: 'inline-block',
                  lineHeight: 1.1,
                  paddingBottom: '0.1em',
                  backgroundImage: `linear-gradient(135deg, ${brand.err}, ${brand.saffron})`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                }}
              >
                washed out.
              </em>
            </h1>
            <p className="mt-3 max-w-xl text-[13.5px] leading-relaxed text-ink-dim">
              An unexpected error stopped the page from rendering. Your booking, points, and saved items are safe. Try again, or head somewhere stable.
            </p>

            {this.state.error.message && (
              <details className="mt-5 rounded-xl border border-glass-edge bg-glass p-3 text-[12px] text-ink-dim">
                <summary className="cursor-pointer font-semibold text-ink">Technical detail</summary>
                <pre className="mt-2 overflow-x-auto whitespace-pre-wrap text-[11px] leading-relaxed text-ink-mute">
                  {this.state.error.message}
                </pre>
              </details>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <Button onClick={this.reset}>
                <RotateCcw size={13} /> Try again
              </Button>
              <Button asChild variant="outline">
                <Link to="/" onClick={this.reset}>
                  Back to home <ArrowRight size={13} />
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/business/contact" onClick={this.reset}>
                  <MessageCircle size={12} /> Report
                </Link>
              </Button>
            </div>
          </div>
        </GlassCard>
      </div>
    );
  }
}
